import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ReviewFilters as IReviewFilters, ReviewListParams, ReviewStatus } from '@/types';
import { useReviews, useModerateReview, useDeleteReview, useBulkModerate } from '@/services/review-service';
import { useToast } from '@/hooks/use-toast';
import { usePermissions } from '@/hooks/usePermissions';
import { ReviewFilters } from './ReviewFilters';
import { ReviewDataTable } from './ReviewDataTable';
import { ReviewDetailDialog } from './ReviewDetailDialog';
import { BulkActionsBar } from './BulkActionsBar';
import { Pagination } from './Pagination';
import { Card } from '@/components/ui/card';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function ReviewListPage() {
  const { t } = useTranslation('reviews');
  const { canEdit, canDelete } = usePermissions();
  const [selectedReviews, setSelectedReviews] = useState<string[]>([]);
  const [selectedReviewIndex, setSelectedReviewIndex] = useState<number>(-1);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [filters, setFilters] = useState<IReviewFilters>({ status: [ReviewStatus.Pending] });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const { toast } = useToast();

  const params: ReviewListParams = {
    ...filters,
    page: currentPage,
    pageSize,
    sortBy,
    sortOrder,
  };

  const { data, isLoading, isError, refetch, dataUpdatedAt } = useReviews(params);
  const moderateMutation = useModerateReview();
  const deleteMutation = useDeleteReview();
  const bulkModerateMutation = useBulkModerate();

  const reviews = data?.items ?? [];
  const totalPages = data?.totalPages ?? 1;
  const totalItems = data?.total ?? 0;

  const selectedReview = selectedReviewIndex >= 0 ? reviews[selectedReviewIndex] ?? null : null;

  const handleFiltersChange = (newFilters: IReviewFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleSort = (newSortBy: string, newSortOrder: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  };

  const handleApprove = async (reviewId: string, notes?: string) => {
    try {
      await moderateMutation.mutateAsync({ reviewId, action: 'approve', notes });
      toast({ title: t('status.approved') });
      setSelectedReviews((prev) => prev.filter((id) => id !== reviewId));
    } catch {
      toast({ title: t('errors.approveFailed'), variant: 'destructive' });
    }
  };

  const handleReject = async (reviewId: string, notes?: string) => {
    try {
      await moderateMutation.mutateAsync({ reviewId, action: 'reject', notes });
      toast({ title: t('status.rejected') });
      setSelectedReviews((prev) => prev.filter((id) => id !== reviewId));
    } catch {
      toast({ title: t('errors.rejectFailed'), variant: 'destructive' });
    }
  };

  const handleUnapprove = async (reviewId: string, notes?: string) => {
    try {
      await moderateMutation.mutateAsync({ reviewId, action: 'unapprove', notes });
      toast({ title: t('status.unapproved') });
      setSelectedReviews((prev) => prev.filter((id) => id !== reviewId));
    } catch {
      toast({ title: t('errors.unapproveFailed'), variant: 'destructive' });
    }
  };

  const handleDelete = async (reviewId: string) => {
    try {
      await deleteMutation.mutateAsync(reviewId);
      toast({ title: t('status.deleted') });
      setSelectedReviews((prev) => prev.filter((id) => id !== reviewId));
      setIsDetailOpen(false);
    } catch {
      toast({ title: t('errors.deleteFailed'), variant: 'destructive' });
    }
  };

  const handleBulkApprove = async () => {
    try {
      const response = await bulkModerateMutation.mutateAsync({
        reviewIds: selectedReviews,
        action: 'approve',
      });
      if (response.failed.length > 0) {
        toast({
          title: t('bulk.partialSuccess', {
            successful: response.successful.length,
            failed: response.failed.length
          }),
          variant: 'destructive',
        });
      } else {
        toast({ title: t('bulk.allApproved', { count: response.successful.length }) });
      }
      setSelectedReviews([]);
    } catch {
      toast({ title: t('errors.bulkApproveFailed'), variant: 'destructive' });
    }
  };

  const handleBulkReject = async () => {
    try {
      const response = await bulkModerateMutation.mutateAsync({
        reviewIds: selectedReviews,
        action: 'reject',
      });
      if (response.failed.length > 0) {
        toast({
          title: t('bulk.partialReject', {
            successful: response.successful.length,
            failed: response.failed.length
          }),
          variant: 'destructive',
        });
      } else {
        toast({ title: t('bulk.allRejected', { count: response.successful.length }) });
      }
      setSelectedReviews([]);
    } catch {
      toast({ title: t('errors.bulkRejectFailed'), variant: 'destructive' });
    }
  };

  const handleBulkDelete = async () => {
    const results = await Promise.allSettled(
      selectedReviews.map((reviewId) => deleteMutation.mutateAsync(reviewId))
    );
    const successful = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.length - successful;

    if (failed > 0 && successful === 0) {
      toast({ title: t('errors.bulkDeleteFailed'), variant: 'destructive' });
    } else if (failed > 0) {
      toast({ title: t('bulk.partialDelete', { successful, failed }), variant: 'destructive' });
    } else {
      toast({ title: t('bulk.allDeleted', { count: successful }) });
    }
    setSelectedReviews([]);
  };

  const handleViewDetail = (reviewId: string) => {
    const index = reviews.findIndex((r) => r.reviewId === reviewId);
    if (index >= 0) {
      setSelectedReviewIndex(index);
      setIsDetailOpen(true);
    }
  };

  const handleNavigateReview = useCallback(
    (direction: 'prev' | 'next') => {
      setSelectedReviewIndex((current) => {
        if (direction === 'prev' && current > 0) return current - 1;
        if (direction === 'next' && current < reviews.length - 1) return current + 1;
        return current;
      });
    },
    [reviews.length]
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">{t('page.title')}</h1>
        <p className="text-muted-foreground mt-2">
          {t('page.description')}
        </p>
      </div>

      <ReviewFilters filters={filters} onFiltersChange={handleFiltersChange} />

      {isLoading ? (
        <Card className="p-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground">{t('status.loading')}</p>
          </div>
        </Card>
      ) : isError ? (
        <Card className="p-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <p className="text-muted-foreground">{t('status.loadError')}</p>
            <Button variant="outline" onClick={() => refetch()}>
              {t('status.retry')}
            </Button>
          </div>
        </Card>
      ) : (
        <>
          <ReviewDataTable
            reviews={reviews}
            selectedReviews={selectedReviews}
            onSelectionChange={setSelectedReviews}
            onApprove={handleApprove}
            onReject={handleReject}
            onUnapprove={handleUnapprove}
            onViewDetail={handleViewDetail}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            canEdit={canEdit}
          />

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            lastUpdated={dataUpdatedAt ? new Date(dataUpdatedAt) : undefined}
          />
        </>
      )}

      <ReviewDetailDialog
        review={selectedReview}
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
        onApprove={handleApprove}
        onReject={handleReject}
        onUnapprove={handleUnapprove}
        onDelete={canDelete ? handleDelete : undefined}
        onNavigate={handleNavigateReview}
        hasPrev={selectedReviewIndex > 0}
        hasNext={selectedReviewIndex < reviews.length - 1}
        canEdit={canEdit}
        canDelete={canDelete}
      />

      <BulkActionsBar
        selectedCount={selectedReviews.length}
        onBulkApprove={handleBulkApprove}
        onBulkReject={handleBulkReject}
        onBulkDelete={canDelete ? handleBulkDelete : undefined}
        onClearSelection={() => setSelectedReviews([])}
        canEdit={canEdit}
      />
    </div>
  );
}
